import React, { useContext } from 'react';
import pick from 'lodash/pick';
import { introspectionApi, queryApi } from 'da-graphql-client/web';
import qs from 'qs';
import { DA_LIST_MAX_ROWS } from '../Helpers/Configuration';
const { fetchRootTypeNames, fetchTypesByName, getMaxVersionForBaseName } = introspectionApi;
const {
    fetchEedmLookupValues,
    fetchEedmValuesByAttribute,
    fetchEedmValuesByIds,
} = queryApi;
const ellucianThemeEditor = window.PRELOADED_STATE?.api?.ellucianThemeEditor || 'https://theme.elluciancloud.com';

const isLikelyConnectivityIssue = (errors) => {
    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
        return true;
    }

    const status = errors?.status;
    if (status === 0) {
        return true;
    }

    const message = `${errors?.message || errors?.error || errors?.statusText || ''}`.toLowerCase();
    return message.includes('failed to fetch') ||
        message.includes('networkerror') ||
        message.includes('network error') ||
        message.includes('load failed');
};

const notifyConnectivityIssue = () => {
    if (typeof window === 'undefined') {
        return;
    }

    window.dispatchEvent(new Event('applicant-connectivity-issue'));
};

export const handleError = async (errors, isDataAccessError = false) => {
    if (isLikelyConnectivityIssue(errors)) {
        notifyConnectivityIssue();
    }

    if(errors?.status === 401) {
        window.location.href = '/unauthorized';
    }
    if (Array.isArray(errors) && errors.length > 0) {
        errors = errors[0];
    }
    else if (typeof errors?.json === 'function') {
        try {
            errors = await errors.json();
        } catch {
            errors = {
                status: errors?.status,
                error: errors?.statusText || 'Request failed',
                message: errors?.statusText || 'Request failed',
            };
        }
    }
    const code = isDataAccessError ? (errors?.extensions?.code ? errors.extensions.code : '') : errors.status;
    const name = errors?.name ? errors.name : '';
    const message = errors?.message || errors?.error || '';
    const formatMessageId = isDataAccessError ? handleDataAccessErrors(code) : '';
    return {
        errorText: message,
        errorCode: code,
        formatMessageId,
        name,
    };
};

const handleDataAccessErrors = (errorCode) => {
    if (errorCode === '403' || errorCode === '401') {
        return 'dataAccessNotAuthorized';
    } else if (errorCode === '404' || errorCode === '503') {
        return 'dataAccessDataNotLoaded';
    } else {
        return 'dataAccessGeneralErrorMessage';
    }
};

const graphApi = {
    cacheKey: 'Apply',
    webFetch: {
        graphQL: '/query',
        options: {
            headers: {
                'Content-Type': 'application/json'
            }
        }
    }
};

const localIntrospectionApi = {
    cacheKey: 'Apply',
    webFetch: {
        graphQL: '/query/graphqlIntrospection',
        options: {
            headers: {
                'Content-Type': 'application/json'
            }
        }
    }
};

export const api = {
    edm: {
        /**
         * Upload a file to EDM storage (single file, JSON payload)
         * @param {Object} fileData - File metadata and content
         */
        uploadFile: async (fileData) => {
            try {
                const res = await fetch('/api/edm/storage/files', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
                    body: JSON.stringify(fileData)
                });
                if (!res.ok) throw res;
                return await res.json();
            } catch (err) {
                throw await handleError(err);
            }
        },

        /**
         * Upload a file to EDM storage using multipart (FormData)
         * @param {FormData} formData - FormData containing file(s)
         */
        uploadFileMultipart: async (payload) => {
            try {
                const res = await fetch('/api/edm/storage/files/multipart', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
                    body: JSON.stringify(payload)
                });
                if (!res.ok) throw res;
                return await res.json();
            } catch (err) {
                throw await handleError(err);
            }
        },

        /**
         * Get file content from EDM storage
         * @param {string} fileId - EDM file ID
         */
        getFileContent: async (fileId) => {
            try {
                const res = await fetch(`/api/edm/storage/files/${encodeURIComponent(fileId)}/content`, {
                    method: 'GET',
                    headers: { 'Accept': 'application/json' }
                });
                if (!res.ok) throw res;
                return await res.json();
            } catch (err) {
                throw await handleError(err);
            }
        },

        /**
         * Delete a file from EDM storage
         * @param {string} fileId - EDM file ID
         */
        deleteFile: async (fileId) => {
            try {
                const res = await fetch(`/api/edm/storage/files/${fileId}`, {
                    method: 'DELETE',
                    headers: { 'Accept': 'application/json' }
                });
                if (!res.ok) throw res;
                return await res.json();
            } catch (err) {
                throw await handleError(err);
            }
        },

        /**
         * Setup EDM workspace
         * @param {Object} workspaceData - Workspace setup payload
         */
        setupWorkspace: async (workspaceData) => {
            try {
                const res = await fetch('/api/edm/storage/setupWorkspace', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
                    body: JSON.stringify(workspaceData)
                });
                if (!res.ok) throw res;
                return await res.json();
            } catch (err) {
                throw await handleError(err);
            }
        },

        lookupFileByDocumentId: async (documentId) => {
            try {
                if (!documentId) {
                    throw new Error('documentId is required');
                }
                const res = await fetch(`/api/edm/storage/files/by-document-id/${encodeURIComponent(documentId)}`, {
                    method: 'GET',
                    headers: { 'Accept': 'application/json' }
                });
                if (res.status === 404) {
                    return null;
                }
                if (!res.ok) throw res;
                return await res.json();
            } catch (err) {
                throw await handleError(err);
            }
        },

        /**
         * Upload file using parallel chunked upload
         * Server handles upload session management (init, complete, cancel) automatically
         * @param {File} file - Browser File object
         * @param {Object} metadata - File metadata (name, mimeType, appId, prospectId, etc)
         * @param {Object} options - { chunkSize, concurrency, onProgress, onChunkComplete }
         * @returns {Promise<Object>} - EDM file response
         */
        uploadFileChunked: async (file, metadata, options = {}) => {
            const chunkSize = options.chunkSize || 5 * 1024 * 1024; // 5MB default
            const concurrency = 1;
            const retryCount = options.retryCount || 3;
            const { onProgress, onChunkComplete } = options;
            
            const totalChunks = Math.ceil(file.size / chunkSize);
            let uploadedChunks = 0;
            let uploadedBytes = 0;
            const documentId = metadata.documentId;

            if (!documentId) {
                throw new Error('metadata.documentId is required for chunked uploads');
            }

            try {
                // Step 1: Initialize multipart upload
                const initRes = await fetch('/api/edm/storage/files/init-multipart', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                        name: metadata.name || file.name,
                        mimeType: metadata.mimeType || file.type,
                        appId: metadata.appId,
                        prospectId: metadata.prospectId,
                        documentId
                    })
                });

                if (!initRes.ok) {
                    const errorData = await initRes.json().catch(() => ({ error: 'Failed to initialize upload' }));
                    throw new Error(errorData.error || 'Failed to initialize upload');
                }

                const { uploadId } = await initRes.json();

                // Step 2: Upload chunks in parallel
                const uploadChunk = async (chunkIndex) => {
                    const start = (chunkIndex - 1) * chunkSize;
                    const end = Math.min(start + chunkSize, file.size);
                    const chunkBlob = file.slice(start, end);
                    
                    for (let attempt = 1; attempt <= retryCount; attempt++) {
                        try {
                            const queryParams = new URLSearchParams({
                                uploadId,
                                chunkIndex,
                                mimeType: metadata.mimeType || file.type
                            });

                            const res = await fetch(`/api/edm/storage/files/upload-chunk?${queryParams.toString()}`, {
                                method: 'POST',
                                headers: {
                                    'Content-Type': 'application/octet-stream'
                                },
                                body: chunkBlob
                            });
                            
                            if (!res.ok) {
                                const errorData = await res.json().catch(() => ({ error: 'Chunk upload failed' }));
                                throw new Error(errorData.error || errorData.message || `Chunk ${chunkIndex} upload failed`);
                            }
                            
                            uploadedChunks++;
                            uploadedBytes += chunkBlob.size;
                            
                            if (onProgress) {
                                onProgress({
                                    uploadedBytes,
                                    totalBytes: file.size,
                                    uploadedChunks,
                                    totalChunks,
                                    percentage: Math.round((uploadedBytes / file.size) * 100)
                                });
                            }
                            
                            if (onChunkComplete) {
                                onChunkComplete({ index: chunkIndex, size: chunkBlob.size });
                            }
                            
                            return await res.json();
                        } catch (err) {
                            if (attempt === retryCount) {
                                console.error(`Failed to upload chunk ${chunkIndex} after ${retryCount} attempts`, err);
                                // Cancel the upload on final failure
                                try {
                                    await fetch(`/api/edm/storage/files/cancel-multipart/${uploadId}`, {
                                        method: 'DELETE'
                                    });
                                } catch (cancelErr) {
                                    console.error('Failed to cancel upload', cancelErr);
                                }
                                throw err;
                            }
                            // Exponential backoff
                            await new Promise(resolve => setTimeout(resolve, 1000 * Math.pow(2, attempt - 1)));
                        }
                    }
                };

                // Execute with concurrency limit - upload chunks in parallel
                const chunkIndices = Array.from({ length: totalChunks }, (_, i) => i + 1);
                let index = 0;
                
                const executeNext = async () => {
                    while (index < chunkIndices.length) {
                        const currentIndex = index++;
                        await uploadChunk(chunkIndices[currentIndex]);
                    }
                };
                
                // Upload chunks in parallel with concurrency control
                await Promise.all(Array.from({ length: Math.min(concurrency, totalChunks) }, () => executeNext()));

                // Step 3: Complete the upload
                const completeRes = await fetch('/api/edm/storage/files/complete-multipart', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({ uploadId, documentId })
                });

                if (!completeRes.ok) {
                    const errorData = await completeRes.json().catch(() => ({ error: 'Failed to complete upload' }));
                    throw new Error(errorData.error || 'Failed to complete upload');
                }

                return await completeRes.json();

            } catch (err) {
                throw await handleError(err);
            }
        }
    },
    decisions: {
        getStatus: () => {
            return fetch('/api/decision/statuses', {
                method: 'GET',
                headers: {
                    'Accept': 'application/json',
                },
            }).then(res => {
                if (res.ok) {
                    return res.json();
                }
                throw res;
            }).catch(err => {
                throw handleError(err);
            });
        },
        updateOfferStatusById: (application, offerStatus, applicantComment='') => {
            return fetch(`/api/decision/application/${application.id}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ offerStatus: offerStatus, offerComment: applicantComment }),
            }).then(res => {
                if (res.ok) {
                    return res.json();
                }
                throw res;
            }).catch(err => {
                throw handleError(err);
            });
        }
    },
    payments: {
        getRedirectUrl: application => {
            return fetch('/api/eps/redirect', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({applicationId : application.id, callbackUrl : window.location.href.split('?')[0]}),
            }).then(res => {
                if (res.ok) {
                    return res.json();
                }
                throw res;
            }).catch(err => {
                throw handleError(err);
            });
        },
        getPaymentStatus: application => {
            return fetch(`/api/eps/status/${application.id}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                },
            }).then(res => {
                if (res.ok) {
                    return res.json();
                }
                throw res;
            }).catch(err => {
                throw handleError(err);
            });
        },
    },
    mfaAuth: {
        checkUser: () => {
            return fetch('/api/totp/check-user', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
            }).then(res => {
                if (res.ok) {
                    return res.json();
                }
                throw res;
            }).catch(err => {
                throw handleError(err);
            });
        },
        authUser: (otpSecret, token) => {
            return fetch('/api/totp/auth-user', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    otpSecret,
                    token
                }),
            }).then(res => {
                if (res.ok) {
                    return res.json();
                }
                throw res;
            }).catch(err => {
                throw handleError(err);
            });
        },
        userReset: (backupCode) => {
            return fetch('/api/totp/userReset', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    backupCode
                }),
            }).then(res => {
                if (res.ok) {
                    return res.json();
                }
                throw res;
            }).catch(err => {
                throw handleError(err);
            });
        },
        mfaReset: () => {
            return fetch('/api/totp/mfaReset', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
            }).then(res => {
                if (res.ok) {
                    return res.json();
                }
                throw res;
            });
        },
    },
    applicationTemplates: {
        getById: id => {
            return fetch(`/api/applicationtemplates/template-id/${id}`, {
                method: 'GET',
                headers: {
                    'Accept': 'application/json',
                },
            }).then(res => {
                if (res.ok) {
                    return res.json();
                }
                throw res;
            }).catch(err => {
                throw handleError(err);
            });
        },
        getAll: () => {
            return fetch('/api/applicationtemplates/summaries', {
                method: 'GET',
                headers: {
                    'Accept': 'application/json',
                },
            }).then(res => {
                if (res.ok) {
                    return res.json();
                }
                throw res;
            }).catch(err => {
                throw handleError(err);
            });
        },
        getLatestPublishedTemplate: (originalTemplateId) => {
            return fetch(`/api/applicationtemplates/latest/${originalTemplateId}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                },
            }).then(res => {
                if (res.ok) {
                    return res.json();
                }
                throw res;
            }).catch(err => {
                throw handleError(err);
            });
        }
    },
    applicationForms: {
        autosave: application => {
            const payload = Object.assign({}, pick(application, ['id', 'revision', 'values', 'codes']));
            if (application.supplementalItems) payload.supplementalItems = application.supplementalItems;
            return fetch(`/api/applications/autosave/${application.id}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(payload),
            }).then(res => {
                if (res.ok) {
                    return res.json();
                }
                throw res;
            }).catch(err => {
                throw handleError(err);
            });
        },
        create: (templateId, values = {}) => {
            return fetch('/api/applications', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    templateId,
                    values,
                }),
            }).then(res => {
                if (res.ok) {
                    return res.json();
                }
                throw res;
            }).catch(err => {
                throw handleError(err);
            });
        },
        delete: application => {
            return fetch(`/api/applications/${application.id}`, {
                method: 'DELETE'
            }).then(res => {
                if (res.ok) {
                    return Promise.resolve(true);
                }
                throw res;
            }).catch(err => {
                throw handleError(err);
            });
        },
        getById: id => {
            return fetch(`/api/applications/application-id/${id}`, {
                method: 'GET',
                headers: {
                    'Accept': 'application/json'
                },
            }).then(res => {
                if (res.ok) {
                    return res.json();
                }
                throw res;
            }).catch(err => {
                throw handleError(err);
            });
        },
        validateApplication: async application => {
            return fetch('/api/applications/validate-application', {
                method: 'PUT',
                headers: {
                    'Accept': 'application/json'
                },
                body: JSON.stringify(application),
            }).then(res => {
                if (res.ok) {
                    return res.json();
                }
                throw res;
            }).catch(async err => {
                throw await handleError(err);
            });
        },
        retrieve: () => {
            return new Promise((resolve, reject) => {
                const result = window.localStorage.getItem('applicant-apply-autosave');
                if (!result) {
                    reject(true);
                }
                resolve(JSON.parse(result));
            });
        },
        update: (application, options = {}) => {
            const queryParams = options.scope ? `?scope=${options.scope}` : '';
            return fetch(`/api/applications${queryParams}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(application),
            }).then(res => {
                if (res.ok) {
                    return res.json();
                }
                throw res;
            }).catch(err => {
                throw handleError(err);
            });
        },
        withdraw: id => {
            return fetch(`/api/applications/withdraw/${id}`, {
                method: 'PUT',
                headers: {
                    'Accept': 'application/json'
                },
            }).then(res => {
                if (res.ok) {
                    return res.json();
                }
                throw res;
            }).catch(err => {
                throw handleError(err);
            });
        },
        savePayment: application => {
            return fetch(`/api/applications/payment/${application.id}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(application),
            }).then(res => {
                if (res.ok) {
                    return res.json();
                }
                throw res;
            }).catch(err => {
                throw handleError(err);
            });
        },
        validateDiscountCode: (code, id) => {
            return fetch(`/api/applications/${id}/validate-discount-code/${code}`, {
                method: 'GET',
                headers: {
                    'Accept': 'application/json',
                },
            }).then(res => {
                if (res.ok) {
                    return res.json();
                }
                throw res;
            }).catch(err => {
                throw handleError(err);
            });
        },
        getAll: () => {
            return fetch('/api/applications/prospect-applications', {
                method: 'GET',
                headers: {
                    'Accept': 'application/json',
                },
            }).then(res => {
                if (res.ok) {
                    return res.json();
                }
                throw res;
            }).catch(err => {
                throw handleError(err);
            });
        },
        checkForSubmittedPayment: id => {
            return fetch(`/api/applications/applicant-submitted-payment/${id}`, {
                method: 'GET',
                headers: {
                    'Accept': 'application/json',
                },
            }).then(res => {
                if (res.ok) {
                    return res.json();
                }
                throw res;
            }).catch(err => {
                throw handleError(err);
            });
        },
        getCurriculumRules: async (term) => {
            let url = '/api/query/curriculum-rules';
            if (term) {
                url = `${url}?term=${term}`;
            }
            return fetch(url, {
                method: 'GET',
                headers: {
                    'Accept': 'application/json',
                },
            }).then(res => {
                if (res.ok) {
                    return res.json();
                }
                throw res;
            }).catch(err => {
                throw handleError(err);
            });
        },
    },
    supplementalItems: {
        /**
         * Soft-delete a submitted file from supplementalItems
         * @param {string} applicationId - Application ID
         * @param {string} documentId - Document ID to delete
         * @returns {Promise<Object>} - { questionId, documentId, status, documentReviewStatus }
         */
        deleteSubmittedFile: async (applicationId, documentId) => {
            try {
                const res = await fetch(`/api/applications/${applicationId}/supportingdocuments/files/${documentId}`, {
                    method: 'PATCH',
                    headers: {
                        'Content-Type': 'application/json',
                        'Accept': 'application/json'
                    },
                    body: JSON.stringify({}),
                });
                if (!res.ok) throw res;
                return await res.json();
            } catch (err) {
                throw await handleError(err);
            }
        },
        /**
         * Store newly uploaded document metadata to supplementalItems
         * @param {string} applicationId - Application ID
         * @param {string} questionId - Question ID associated with the document
         * @param {string} documentId - Unique document ID
         * @param {string} edmFileId - EDM file ID
         * @param {string} fileName - Original file name
         * @param {number} fileSize - File size in bytes
         * @param {string} mimeType - MIME type of the file
         * @returns {Promise<Object>} - { questionId, documentId, edmFileId, uploadedAt }
         */
        storeUploadedDocument: async (applicationId, { questionId, documentId, edmFileId, fileName, fileSize, mimeType }) => {
            try {
                const url = `/api/applications/${applicationId}/supplemental-items`;
                const payload = { questionId, documentId, edmFileId, fileName, fileSize, mimeType };
                const res = await fetch(url, {
                    method: 'PATCH',
                    headers: {
                        'Content-Type': 'application/json',
                        'Accept': 'application/json'
                    },
                    body: JSON.stringify(payload),
                });
                if (!res.ok) {
                    throw res;
                }
                const result = await res.json();
                return result;
            } catch (err) {
                throw await handleError(err);
            }
        },

        resubmitSupplementalQuestion: async (applicationId, questionId) => {
            try {
                const res = await fetch(`/api/applications/${applicationId}/supplemental-items/${questionId}/resubmit`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Accept': 'application/json'
                    },
                    body: JSON.stringify({}),
                });
                if (!res.ok) throw res;
                return await res.json();
            } catch (err) {
                throw await handleError(err);
            }
        },

        addApplicantComment: async (applicationId, questionId, comment) => {
            try {
                const payload = { comment };
                const res = await fetch(`/api/applications/${applicationId}/supportingdocuments/questions/${questionId}/comments`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Accept': 'application/json'
                    },
                    body: JSON.stringify(payload),
                });
                if (!res.ok) throw res;
                return await res.json();
            } catch (err) {
                throw await handleError(err);
            }
        }
    },
    inquiries: {
        createInquiry: (inquiry, recaptchaToken, recaptchaAction) => {
            const params = qs.stringify({
                recaptcha_token: recaptchaToken,
                action: recaptchaAction,
            });
            return fetch(`/api/applications/inquiries?${params}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(inquiry),
            }).then(res => {
                if (res.ok) {
                    return res.json();
                }
                throw res;
            }).catch(err => {
                throw handleError(err);
            });
        },
        retrieve: async (model, recaptchaToken, recaptchaAction, bypass) => {
            const params = new URLSearchParams({
                recaptcha_token: recaptchaToken,
                action: recaptchaAction,
            });
            if (bypass) {
                params.set('bypass', 'true');
            }
            const url = `/api/applications/retrieve?${params.toString()}`;
            return fetch(url, {
                method: 'POST',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(model),
            }).then(async res => {
                if (res.ok) {
                    //App Retrieval never returns anything unless you are using the bypass but that is consumed in server.js
                    //     so don't call res.json () like we do elsewhere in this file
                    return true;
                }
                const errorRes = await res.json();
                errorRes.status = res.status;
                throw errorRes;
            }).catch(async err => {
                throw await handleError(err);
            });
        },
        resendInquiryEmail: (inquiryId, locale) => {
            const url = `/api/applications/inquiries/resendEmail/${inquiryId}`;
            return fetch(url, {
                method: 'POST',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ locale }),
            }).then(res => {
                if (res.ok) {
                    return res.json();
                }
                throw res;
            }).catch(err => {
                throw handleError(err);
            });
        },
    },
    recaptchaToken: {
        get: () => {
            return fetch('/api/tenants/recaptcha-tokens', {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json'
                },
            }).then(res => {
                if (res.ok) {
                    return res.json();
                }
                throw res;
            }).catch(err => {
                throw handleError(err);
            });
        },
    },
    maintenance: {
        getStatus: () => {
            return fetch('/api/tenants/maintenance-status', {
                method: 'GET',
                headers: {
                    'Accept': 'application/json',
                },
                cache: 'no-store',
            }).then(res => {
                if (res.ok) {
                    return res.json();
                }
                throw res;
            }).catch(err => {
                throw handleError(err);
            });
        },
    },
    theme: {
        get: (themeName, appId = window.PRELOADED_STATE.themeEditorAppId) => {
            if (!appId || !themeName) {
                return Promise.reject('Ellucian Theme Editor app id and theme name must be provided');
            }
            const url = `${ellucianThemeEditor}/${appId}/theme/get?name=${themeName}`;
            return fetch(url, {
                method: 'GET',
            })
                .then(res => {
                    if (res.ok) {
                        return res.json();
                    }
                    throw res;
                })
                .catch(err => {
                    throw handleError(err);
                });
        },
    },
    dataAccess: {
        getRootTypes: async () => {
            try {
                const res = await fetchRootTypeNames(localIntrospectionApi);
                return res;
            }
            catch (err) {
                throw handleError(err, true);
            }
        },
        getTypes: async graphTypes => {
            try {
                await fetchRootTypeNames(localIntrospectionApi);
                const res = await fetchTypesByName(graphApi, graphTypes, true);
                return res;
            }
            catch (err) {
                throw handleError(err, true);
            }
        },
        getLookupValues: async (graphType, filter, sort, sortOptions = { rowsPerPage: DA_LIST_MAX_ROWS }) => {
            try {
                graphType = graphType?.startsWith('F_') ? graphType.substring(2) : graphType;
                await fetchRootTypeNames(localIntrospectionApi);
                const res = await fetchEedmLookupValues(graphApi, graphType, filter, sort, sortOptions);
                return res;
            }
            catch (err) {
                throw handleError(err, true);
            }
        },
        getAttributeValues: async (graphType, attributes, filter, sort, sortOptions = { rowsPerPage: DA_LIST_MAX_ROWS }, includeTotalCount = false) => {
            try {
                await fetchRootTypeNames(localIntrospectionApi);
                const res = await fetchEedmValuesByAttribute(graphApi, graphType, attributes, filter, sort, sortOptions, includeTotalCount);
                return res;
            }
            catch (err) {
                throw handleError(err, true);
            }
        },
        getMaxVersionByGraphType: async (graphType) => {
            try {
                graphType = !graphType?.startsWith('F_') ? 'F_' + graphType : graphType;
                await fetchRootTypeNames(localIntrospectionApi);
                const res = await getMaxVersionForBaseName(graphApi, graphType);
                return res;
            }
            catch (err) {
                throw await handleError(err, true);
            }
        },
        getValuesByIds: async (graphType, idList, attributes) => {
            try {
                await fetchRootTypeNames(localIntrospectionApi);
                const res = await fetchEedmValuesByIds(graphApi, graphType, idList, attributes);
                return res;
            }
            catch (err) {
                throw handleError(err, true);
            }
        },
        graphApi,
        localIntrospectionApi
    },
    communicate: {
        getVerifiedEmails: async (senderEmail) => {
            const params = new URLSearchParams({
                senderEmail: senderEmail || ''
            });
            const url = `/api/communicate/email-verifications/verified?${params.toString()}`;
            return fetch(url, {
                method: 'GET',
            }).then(res => {
                if (res.ok) {
                    return res.json();
                }
                throw res;
            })
            .catch(async err => {
                throw await handleError(err);
            });
        },
        sendSupplementalEmail: async ({ applicationId, documentType, locale, emailType }) => {
            return fetch('/api/communicate/supplemental-email', {
                method: 'POST',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    applicationId,
                    documentType,
                    locale,
                    emailType
                }),
            }).then(res => {
                if (res.ok) {
                    return res.json();
                }
                throw res;
            })
            .catch(async err => {
                throw await handleError(err);
            });
        }
    },
    preferences: {
        getLanguagePreference: () => {
            return fetch('/api/language-preference', {
                method: 'GET',
                headers: {
                    'Accept': 'application/json',
                },
            }).then(res => {
                if (res.ok) {
                    return res.json();
                }
                throw res;
            }).catch(async (err) => {
                throw await handleError(err);
            });
        },
        setLanguagePreference: (locale) => {
            return fetch('/api/language-preference', {
                method: 'POST',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ locale }),
            }).then(res => {
                if (res.ok) {
                    return res.json();
                }
                throw res;
            }).catch(err => {
                throw handleError(err);
            });
        },
    },
    applicantLogout: () => {
        return fetch('/applicantlogout', {
            method: 'GET'
        }).catch(err => {
            throw handleError(err);
        });
    }
};
const ApiContext = React.createContext(api);
const ApiProvider = props => {
    return (
        <ApiContext.Provider value={api}>{props.children}</ApiContext.Provider>
    );
};
const useApi = () => {
    const ctx = useContext(ApiContext);
    if (ctx === undefined) {
        throw new Error('useApi must be used within a ApiProvider');
    }
    return ctx;
};
export { ApiProvider, useApi };
